/**
 * Template Registry
 * Central hub for all built-in and custom templates
 */

import { nanoid } from 'nanoid';
import type { Template } from '@/types/template';
import type { Layer } from '@/types/layers';
import {
  createEclipseTemplate,
  createSplitTemplate,
  createTypeTemplate,
} from '@/lib/migration/styleToTemplate';

// Import JSON templates
import gradientBoldJson from './builtin/gradient-bold.json';
import productSpotlightJson from './builtin/product-spotlight.json';
import saleBannerJson from './builtin/sale-banner.json';
import editorialJson from './builtin/editorial.json';
import neonGlowJson from './builtin/neon-glow.json';
import testimonialJson from './builtin/testimonial.json';
import appShowcaseJson from './builtin/app-showcase.json';
import eventAnnouncementJson from './builtin/event-announcement.json';
import statsNumbersJson from './builtin/stats-numbers.json';
import minimalistJson from './builtin/minimalist.json';
import splitPhotoJson from './builtin/split-photo.json';
import geometricAbstractJson from './builtin/geometric-abstract.json';
import retroVintageJson from './builtin/retro-vintage.json';
import darkLuxuryJson from './builtin/dark-luxury.json';
import comparisonJson from './builtin/comparison.json';
import privacyCampaignJson from './builtin/privacy-campaign.json';

/**
 * Hydrate a template loaded from JSON by adding unique IDs to layers
 * Since JSON can't contain function calls like nanoid(), we add IDs at runtime
 */
function hydrateTemplate(json: unknown): Template {
  const template = json as Template;
  return {
    ...template,
    layers: template.layers.map((layer) => ({
      ...layer,
      id: nanoid(),
    })) as Layer[],
  };
}

// Built-in templates (generated from style components + JSON files)
const BUILTIN_TEMPLATES: Template[] = [
  // Original templates from style components
  createEclipseTemplate(),
  createSplitTemplate(),
  createTypeTemplate(),
  // Templates from JSON files
  hydrateTemplate(gradientBoldJson),
  hydrateTemplate(productSpotlightJson),
  hydrateTemplate(saleBannerJson),
  hydrateTemplate(editorialJson),
  hydrateTemplate(neonGlowJson),
  hydrateTemplate(testimonialJson),
  hydrateTemplate(appShowcaseJson),
  hydrateTemplate(eventAnnouncementJson),
  hydrateTemplate(statsNumbersJson),
  hydrateTemplate(minimalistJson),
  hydrateTemplate(splitPhotoJson),
  hydrateTemplate(geometricAbstractJson),
  hydrateTemplate(retroVintageJson),
  hydrateTemplate(darkLuxuryJson),
  hydrateTemplate(comparisonJson),
  hydrateTemplate(privacyCampaignJson),
];

// Template registry
let customTemplates: Template[] = [];

/**
 * Get all available templates
 */
export function getAllTemplates(): Template[] {
  return [...BUILTIN_TEMPLATES, ...customTemplates];
}

/**
 * Get built-in templates only
 */
export function getBuiltinTemplates(): Template[] {
  return BUILTIN_TEMPLATES;
}

/**
 * Get custom templates only
 */
export function getCustomTemplates(): Template[] {
  return customTemplates;
}

/**
 * Get a template by ID
 */
export function getTemplateById(id: string): Template | undefined {
  return getAllTemplates().find((t) => t.metadata.id === id);
}

/**
 * Get templates by category
 */
export function getTemplatesByCategory(category: Template['metadata']['category']): Template[] {
  return getAllTemplates().filter((t) => t.metadata.category === category);
}

/**
 * Add a custom template
 */
export function addCustomTemplate(template: Template): void {
  // Ensure it's marked as custom
  template.metadata.isBuiltin = false;
  customTemplates.push(template);
}

/**
 * Remove a custom template
 */
export function removeCustomTemplate(id: string): boolean {
  const index = customTemplates.findIndex((t) => t.metadata.id === id);
  if (index !== -1) {
    customTemplates.splice(index, 1);
    return true;
  }
  return false;
}

/**
 * Update a custom template
 */
export function updateCustomTemplate(id: string, updates: Partial<Template>): boolean {
  const template = customTemplates.find((t) => t.metadata.id === id);
  if (template) {
    Object.assign(template, updates);
    template.metadata.updatedAt = new Date().toISOString();
    return true;
  }
  return false;
}

/**
 * Load custom templates from storage
 */
export function loadCustomTemplates(templates: Template[]): void {
  customTemplates = templates.filter((t) => !t.metadata.isBuiltin);
}

/**
 * Clear all custom templates
 */
export function clearCustomTemplates(): void {
  customTemplates = [];
}

// Default export
export default {
  getAllTemplates,
  getBuiltinTemplates,
  getCustomTemplates,
  getTemplateById,
  getTemplatesByCategory,
  addCustomTemplate,
  removeCustomTemplate,
  updateCustomTemplate,
  loadCustomTemplates,
  clearCustomTemplates,
};
